// hooks/useRegisterTabContext.js
import { useEffect, useRef } from 'react';
import { useTabContext } from './useTabContext';

/**
 * Hook to automatically register a tab's context when it mounts
 * Updates the global tab context with the tab's default values
 * 
 * @param {string} tabName - Unique identifier for the tab
 * @param {Object} defaultValues - Default values for this tab
 * @param {Object} options - Configuration options
 * @param {boolean} options.clearOnUnmount - Clear context when component unmounts
 * @param {Array} options.dependencies - Dependencies to watch for updates
 * 
 * @example
 * useRegisterTabContext('brand-monitoring', {
 *   execution_date: selectedDate,
 *   brand: selectedBrand,
 *   brandContext: 'monitoring'
 * });
 */
export const useRegisterTabContext = (
  tabName,
  defaultValues,
  options = {}
) => {
  const { 
    clearOnUnmount = false, 
    dependencies = [] 
  } = options;
  
  const { updateTabContext, clearTabContext } = useTabContext();
  const isFirstMount = useRef(true);
  const previousTabName = useRef(tabName);

  useEffect(() => {
    // Register tab context on mount or when dependencies change
    const contextData = {
      tabName,
      ...defaultValues,
    };

    // Always update on first mount or when tab name changes
    if (isFirstMount.current || previousTabName.current !== tabName) {
      updateTabContext(contextData);
      isFirstMount.current = false;
      previousTabName.current = tabName;
    } else {
      // Only update if dependencies provided and changed
      if (dependencies.length > 0) {
        updateTabContext(contextData);
      }
    }

    // Cleanup: clear context on unmount if specified
    return () => {
      if (clearOnUnmount) {
        clearTabContext();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabName, ...dependencies, updateTabContext, clearTabContext, clearOnUnmount]);
};

/**
 * Hook to update tab context field dynamically
 * Useful for updating individual fields without re-registering entire context
 * 
 * @param {string} field - Field name to update
 * @param {any} value - New value for the field
 * 
 * @example
 * const [selectedDate, setSelectedDate] = useState('2024-01-15');
 * useUpdateTabField('execution_date', selectedDate);
 */
export const useUpdateTabField = (field, value) => {
  const { updateTabField } = useTabContext();
  const previousValue = useRef(value);

  useEffect(() => {
    if (previousValue.current !== value) {
      updateTabField(field, value);
      previousValue.current = value;
    }
  }, [field, value, updateTabField]);
};